import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Layout} from '@/components/Layout';
import {StatusBadge} from '@/components/StatusBadge';
import {LocationCard} from '@/components/LocationCard';
import {ChargerSpecsCard} from '@/components/ChargerSpecsCard';
import {QRCodeSVG} from 'qrcode.react';
import {ArrowLeft, Download, MapPin, User, Clock, Activity, Printer, ExternalLink, Zap, Loader2, AlertTriangle} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {toast} from 'sonner';
import {api, getApiErrorMessage, occurrenceEndpoints, tagEndpoints, ticketEndpoints} from '@/lib/api';

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const unwrapAttributes = (source = {}) => {
  const value = source?.data && typeof source.data === 'object' && !Array.isArray(source.data)
    ? source.data
    : source;
  return value?.attributes && typeof value.attributes === 'object' && !Array.isArray(value.attributes)
    ? {...value, ...value.attributes}
    : value;
};

const firstValue = (...values) => values.find(value => value !== undefined && value !== null && value !== '');

const dateTimeFrom = (...values) => {
  const value = firstValue(...values);
  return value ? String(value).replace('T', ' ').slice(0, 16) : '';
};

const dateFrom = (...values) => {
  const value = firstValue(...values);
  return value ? String(value).slice(0, 10) : '';
};

const numberFrom = (...values) => {
  const value = firstValue(...values);
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeTagStatus = (tag, resource) => {
  if (tag.deactivated_at || tag.gest_tag_deactivation_reason_id || tag.deactivate_reason_id || tag.deactivation_reason_id || tag.deativation_reason_id) return 'inativa';
  if (tag.gest_resource_id || tag.resource_id || resource?.id) return 'atribuida';

  const value = String(tag.status ?? '').toLowerCase();
  const map = {
    0: 'armazem',
    1: 'atribuida',
    2: 'inativa',
    available: 'armazem',
    warehouse: 'armazem',
    armazem: 'armazem',
    assigned: 'atribuida',
    atribuida: 'atribuida',
    active: 'ativa',
    ativa: 'ativa',
    inactive: 'inativa',
    inativa: 'inativa',
  };
  return map[value] || 'armazem';
};

const managerNameFrom = (resource = {}) => {
  const manager = unwrapAttributes(resource.company_manager ?? resource.manager ?? resource.driver ?? resource.user ?? {});
  return manager.name ?? manager.full_name ?? manager.display_name ?? resource.manager_name ?? resource.driver_name ?? '-';
};

const coordinatesFrom = (...sources) => {
  for (const source of sources) {
    const item = unwrapAttributes(source || {});
    const lat = firstValue(item.latitude, item.lat, item.gps_latitude, item.location_latitude);
    const lng = firstValue(item.longitude, item.lng, item.lon, item.gps_longitude, item.location_longitude);
    if (lat !== undefined && lng !== undefined) {
      const parsedLat = Number(lat);
      const parsedLng = Number(lng);
      if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
        return {lat: parsedLat, lng: parsedLng};
      }
    }
  }
  return null;
};

const normalizeChargerSpecs = (tag = {}, resource = {}) => {
  const charger = unwrapAttributes(tag.charger_specs ?? tag.chargerSpecs ?? tag.charger ?? resource.charger_specs ?? resource.charger ?? {});
  if (!charger || Object.keys(charger).length === 0) return null;

  const connectors = collectionPayload(charger, 'connectors').length
    ? collectionPayload(charger, 'connectors')
    : String(charger.connectors ?? '').split(',').map(item => item.trim()).filter(Boolean);

  return {
    status: charger.status ?? charger.availability_status ?? 'available',
    power: firstValue(charger.power, charger.max_power, charger.power_kw, charger.max_power_kw, ''),
    connectors,
    currentType: firstValue(charger.current_type, charger.currentType, charger.phase_type, ''),
    totalCharges: numberFrom(charger.total_charges, charger.totalCharges, charger.sessions_count),
    totalKwh: numberFrom(charger.total_kwh, charger.totalKwh, charger.energy_total_kwh),
    availability: numberFrom(charger.availability, charger.availability_rate),
    installationDate: dateFrom(charger.installation_date, charger.installationDate, charger.created_at),
    lastMaintenance: dateFrom(charger.last_maintenance, charger.lastMaintenance),
    nextMaintenance: dateFrom(charger.next_maintenance, charger.nextMaintenance),
    currentSession: charger.current_session ?? charger.currentSession ?? null,
  };
};

const tagReferenceFrom = (tag = {}, fallback = '') =>
  String(firstValue(tag.reference, tag.serial_no, tag.code, tag.tag_code, tag.id, fallback) ?? '');

const normalizeTagDetail = (source = {}, fallbackId = '') => {
  const tag = unwrapAttributes(source);
  const batch = unwrapAttributes(tag.gest_tag_batch ?? tag.tag_batch ?? tag.batch ?? {});
  const objectType = unwrapAttributes(batch.object_type ?? tag.object_type ?? {});
  const resource = unwrapAttributes(tag.gest_resource ?? tag.resource ?? {});
  const id = tagReferenceFrom(tag, fallbackId);
  const category = firstValue(objectType.name, objectType.label, objectType.title, batch.object_type_name, tag.category, 'Sem tipo de objeto');
  const location = firstValue(tag.placement, tag.location, tag.location_name, resource.location, resource.location_name, resource.address, '-');
  const coordinates = coordinatesFrom(tag, resource);
  const history = normalizeHistoryItems(
    collectionPayload(tag, 'occurrences')
      .concat(collectionPayload(tag, 'incidents'))
      .concat(collectionPayload(tag, 'tickets')),
    id,
    tag.id,
    {includeMissingTag: true}
  );

  return {
    rawId: tag.id ?? fallbackId,
    id,
    category,
    status: normalizeTagStatus(tag, resource),
    user: managerNameFrom(resource),
    location,
    coordinates,
    createdAt: dateFrom(tag.created_at),
    updatedAt: dateFrom(tag.updated_at),
    lastIncident: dateTimeFrom(tag.last_incident_at, tag.lastIncident),
    totalIncidents: numberFrom(tag.total_incidents, tag.incidents_count, tag.occurrences_count, tag.tickets_count),
    resolvedIncidents: numberFrom(tag.resolved_incidents, tag.resolvedIncidents, tag.resolved_occurrences_count, tag.resolved_tickets_count),
    avgResolutionTime: firstValue(tag.avg_resolution_time, tag.average_resolution_time, tag.avgResolutionTime, '-'),
    chargerSpecs: normalizeChargerSpecs(tag, resource),
    chargerSessions: collectionPayload(tag, 'charging_sessions').map(normalizeChargingSession).filter(session => session.id),
    history,
  };
};

const normalizeOccurrenceStatus = (status) => {
  const value = String(status ?? '').toLowerCase();
  const map = {
    0: 'pendente',
    1: 'em_progresso',
    2: 'resolvido',
    3: 'ignorada',
    pending: 'pendente',
    open: 'pendente',
    pendente: 'pendente',
    in_progress: 'em_progresso',
    progress: 'em_progresso',
    em_progresso: 'em_progresso',
    resolved: 'resolvido',
    resolvido: 'resolvido',
    resolvida: 'resolvido',
    ignored: 'ignorada',
    ignorada: 'ignorada',
    closed: 'resolvido',
  };
  return map[value] || value || 'pendente';
};

const normalizeHistoryItem = (source = {}, index = 0) => {
  const item = unwrapAttributes(source);
  const occurrence = unwrapAttributes(item.occurrence ?? item.incident ?? {});
  const tag = item.tag ?? occurrence.tag;
  const tagRecord = typeof tag === 'string' || typeof tag === 'number' ? {} : unwrapAttributes(tag ?? {});
  const type = unwrapAttributes(item.occurrence_type ?? item.type ?? occurrence.occurrence_type ?? occurrence.type ?? {});
  const createdAt = dateTimeFrom(item.reported_at, item.occurred_at, item.created_at, item.createdAt, item.date, occurrence.created_at);

  return {
    id: firstValue(item.id, item.uuid, item.code, item.reference, item.ticket_no, `history-${index}`),
    ticket: firstValue(item.code, item.reference, item.ticket_no, occurrence.code, occurrence.reference, item.id, `#${index + 1}`),
    type: firstValue(type.name, type.title, item.type_name, item.occurrence_type_name, item.title, item.subject, occurrence.title, 'Ocorrencia'),
    status: normalizeOccurrenceStatus(item.status ?? occurrence.status),
    date: createdAt,
    timestamp: createdAt ? new Date(createdAt).getTime() : 0,
    duration: firstValue(item.resolution_time, item.duration, item.resolved_in, '-'),
    tagId: firstValue(
      item.tag_code,
      item.tag_reference,
      item.tag_id,
      item.gest_tag_id,
      typeof tag === 'string' || typeof tag === 'number' ? tag : null,
      tagRecord.code,
      tagRecord.reference,
      tagRecord.id
    ),
  };
};

const normalizeHistoryItems = (items = [], tagReference = '', rawTagId = '', {includeMissingTag = false} = {}) =>
  (Array.isArray(items) ? items : [])
    .map(normalizeHistoryItem)
    .filter(item => {
      if (!tagReference && !rawTagId) return true;
      const itemTagId = String(item.tagId ?? '');
      return (includeMissingTag && !itemTagId) || itemTagId === String(tagReference) || itemTagId === String(rawTagId);
    })
    .sort((a, b) => b.timestamp - a.timestamp);

const normalizeChargingSession = (source = {}, index = 0) => {
  const session = unwrapAttributes(source);
  return {
    id: firstValue(session.id, session.uuid, session.code, `session-${index}`),
    label: firstValue(session.code, session.reference, session.id, `#${index + 1}`),
    date: dateTimeFrom(session.started_at, session.created_at, session.date),
    status: firstValue(session.status_label, session.status, ''),
    duration: firstValue(session.duration, session.duration_label, '-'),
    kwh: firstValue(session.kwh, session.energy_kwh, '-'),
    power: firstValue(session.average_power, session.average_power_kw, session.power, '-'),
    cost: firstValue(session.cost, session.total_cost, '-'),
  };
};

const findTagInList = (payload, tagId) => {
  const requested = String(tagId);
  return collectionPayload(payload, 'tags').find((source) => {
    const tag = unwrapAttributes(source);
    return [source.id, tag.id, tag.reference, tag.serial_no, tag.code, tag.uuid]
      .map(value => String(value ?? ''))
      .includes(requested);
  });
};

const historyMatchesTag = (history, tag) => {
  const ids = new Set([tag.id, tag.rawId].map(value => String(value ?? '')).filter(Boolean));
  return history.filter(item => !item.tagId || ids.has(String(item.tagId)));
};

export const TagDetailPage = () => {
  const navigate = useNavigate();
  const {tagId} = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [tagData, setTagData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadTag = async () => {
      setLoading(true);
      setError('');

      try {
        let tagSource = null;

        try {
          const detailResponse = await api.get(tagEndpoints.detail(tagId));
          tagSource = detailResponse.data?.data ?? detailResponse.data?.tag ?? detailResponse.data;
        } catch {
          const listResponse = await api.get(tagEndpoints.list);
          tagSource = findTagInList(listResponse.data, tagId);
        }

        if (!tagSource) {
          throw new Error('Tag nao encontrada.');
        }

        const normalizedTag = normalizeTagDetail(tagSource, tagId);
        const [occurrencesResponse, ticketsResponse] = await Promise.all([
          api.get(occurrenceEndpoints.list).catch(() => ({data: []})),
          api.get(ticketEndpoints.list).catch(() => ({data: []})),
        ]);
        const apiHistory = normalizeHistoryItems(
          collectionPayload(occurrencesResponse.data, 'occurrences')
            .concat(collectionPayload(occurrencesResponse.data, 'incidents'))
            .concat(collectionPayload(ticketsResponse.data, 'tickets')),
          normalizedTag.id,
          normalizedTag.rawId
        );
        const history = historyMatchesTag(normalizedTag.history.concat(apiHistory), normalizedTag);
        const uniqueHistory = Array.from(new Map(history.map(item => [String(item.id), item])).values())
          .sort((a, b) => b.timestamp - a.timestamp);

        if (!cancelled) {
          setTagData({
            ...normalizedTag,
            history: uniqueHistory,
            totalIncidents: normalizedTag.totalIncidents || uniqueHistory.length,
            resolvedIncidents: normalizedTag.resolvedIncidents || uniqueHistory.filter(item => item.status === 'resolvido').length,
            lastIncident: normalizedTag.lastIncident || uniqueHistory[0]?.date || '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setTagData(null);
          setError(getApiErrorMessage(err, err?.message || 'Nao foi possivel carregar a tag.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTag();

    return () => {
      cancelled = true;
    };
  }, [tagId]);

  const qrCodeUrl = tagData ? `${window.location.origin}/report/${tagData.id}` : '';
  const resolutionRate = useMemo(() => {
    if (!tagData?.totalIncidents) return 0;
    return Math.round((tagData.resolvedIncidents / tagData.totalIncidents) * 100);
  }, [tagData]);

  const handleLocationUpdate = (newLocation) => {
    setTagData(prev => prev ? {
      ...prev,
      location: newLocation.location,
      coordinates: newLocation.coordinates,
    } : prev);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg || !tagData) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgUrl = URL.createObjectURL(new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'}));
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 320;
      canvas.height = 320;
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      URL.revokeObjectURL(svgUrl);

      const downloadLink = document.createElement('a');
      downloadLink.href = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
      downloadLink.download = `${tagData.id}_QRCode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success('QR Code descarregado com sucesso!');
    };

    image.src = svgUrl;
  };

  const handlePrintQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg || !tagData) return;

    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) {
      toast.error('Pop-up bloqueado pelo navegador');
      return;
    }

    const doc = printWindow.document;
    const title = doc.createElement('title');
    title.textContent = `QR Code - ${tagData.id}`;
    doc.head.appendChild(title);

    const style = doc.createElement('style');
    style.textContent = 'body{text-align:center;font-family:sans-serif;padding:40px;} h1{margin-bottom:20px;} .location{color:#666;margin-bottom:30px;} .hint{margin-top:30px;font-size:14px;} svg{width:400px;height:400px;}';
    doc.head.appendChild(style);

    const h1 = doc.createElement('h1');
    h1.textContent = tagData.id;
    doc.body.appendChild(h1);

    const locP = doc.createElement('p');
    locP.className = 'location';
    locP.textContent = tagData.location;
    doc.body.appendChild(locP);

    doc.body.appendChild(svg.cloneNode(true));

    const hint = doc.createElement('p');
    hint.className = 'hint';
    hint.textContent = 'Escaneie para reportar problemas';
    doc.body.appendChild(hint);

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Janela de impressao aberta!');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrCodeUrl);
    toast.success('Link copiado para area de transferencia!');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/tags')}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={18}/>
            Voltar
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">
                {tagData?.id || `Tag ${tagId}`}
              </h1>
              {tagData && <StatusBadge status={tagData.status} type="tag"/>}
            </div>
            {tagData?.location && (
              <p className="text-slate-600 font-primary mt-1 flex items-center gap-2">
                <MapPin size={16} className="text-blue-600"/>
                {tagData.location}
              </p>
            )}
          </div>
        </div>

        {loading && (
          <Card className="p-10 text-center text-slate-500">
            <Loader2 className="mx-auto mb-3 animate-spin" size={28}/>
            A carregar tag...
          </Card>
        )}

        {!loading && error && (
          <Card className="p-10 text-center">
            <AlertTriangle className="mx-auto mb-3 text-red-500" size={32}/>
            <p className="font-medium text-slate-900">{error}</p>
          </Card>
        )}

        {!loading && tagData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-sm font-medium">Total Ocorrencias</p>
                  <Activity className="text-blue-600" size={20}/>
                </div>
                <p className="text-3xl font-secondary font-bold text-slate-900">{tagData.totalIncidents}</p>
                <p className="text-xs text-slate-500 mt-1">{tagData.createdAt ? `Desde ${tagData.createdAt}` : 'Sem data de criacao'}</p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-sm font-medium">Resolvidas</p>
                  <Activity className="text-emerald-600" size={20}/>
                </div>
                <p className="text-3xl font-secondary font-bold text-emerald-600">{tagData.resolvedIncidents}</p>
                <p className="text-xs text-slate-500 mt-1">Taxa: {resolutionRate}%</p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-sm font-medium">Tempo Medio</p>
                  <Clock className="text-amber-600" size={20}/>
                </div>
                <p className="text-2xl font-secondary font-bold text-slate-900">{tagData.avgResolutionTime}</p>
                <p className="text-xs text-slate-500 mt-1">Resolucao</p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-sm font-medium">Responsavel</p>
                  <User className="text-purple-600" size={20}/>
                </div>
                <p className="text-lg font-secondary font-bold text-slate-900">{tagData.user}</p>
                <p className="text-xs text-slate-500 mt-1">Atribuido</p>
              </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white border border-slate-200">
                <TabsTrigger value="overview">Visao Geral</TabsTrigger>
                {tagData.chargerSpecs && (
                  <TabsTrigger value="charger">
                    <Zap size={16} className="mr-2"/>
                    Carregador
                  </TabsTrigger>
                )}
                <TabsTrigger value="qrcode">QR Code</TabsTrigger>
                <TabsTrigger value="history">Historico</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <LocationCard tag={tagData} onUpdate={handleLocationUpdate}/>

                  <Card className="p-6">
                    <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">Informacoes da Tag</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">ID:</span>
                        <span className="font-secondary font-bold text-slate-900">{tagData.id}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Categoria:</span>
                        <span className="font-medium text-slate-900">{tagData.category}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Estado:</span>
                        <StatusBadge status={tagData.status} type="tag"/>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Criado em:</span>
                        <span className="font-medium text-slate-900">{tagData.createdAt || '-'}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-slate-600">Ultima Ocorrencia:</span>
                        <span className="font-medium text-slate-900">{tagData.lastIncident || '-'}</span>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="p-6">
                  <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">Ultimas Ocorrencias</h3>
                  <HistoryList history={tagData.history.slice(0, 5)}/>
                </Card>
              </TabsContent>

              {tagData.chargerSpecs && (
                <TabsContent value="charger" className="mt-6">
                  <ChargerSpecsCard charger={tagData.chargerSpecs}/>

                  <Card className="p-6 mt-6">
                    <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">Historico de Carregamentos</h3>
                    {tagData.chargerSessions.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-8">Sem carregamentos devolvidos pela API.</p>
                    ) : (
                      <div className="space-y-3">
                        {tagData.chargerSessions.map(session => (
                          <div key={session.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="text-sm font-medium text-slate-900">Carregamento {session.label}</p>
                                <p className="text-xs text-slate-600">{session.date}</p>
                              </div>
                              {session.status && (
                                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium">
                                  {session.status}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-xs">
                              <SessionMetric label="Duracao" value={session.duration}/>
                              <SessionMetric label="Energia" value={session.kwh === '-' ? '-' : `${session.kwh} kWh`}/>
                              <SessionMetric label="Potencia Media" value={session.power}/>
                              <SessionMetric label="Custo" value={session.cost}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="qrcode" className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="p-8 text-center">
                    <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">QR Code da Tag</h3>
                    <div className="bg-white p-6 rounded-xl border-2 border-slate-200 inline-block mb-6">
                      <QRCodeSVG
                        id="qr-code-svg"
                        value={qrCodeUrl}
                        size={280}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <p className="text-sm text-slate-600 mb-6">
                      Escaneie este codigo para reportar problemas nesta tag
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button onClick={handleDownloadQR} className="bg-blue-600 hover:bg-blue-700">
                        <Download size={16} className="mr-2"/>
                        Descarregar
                      </Button>
                      <Button onClick={handlePrintQR} variant="outline">
                        <Printer size={16} className="mr-2"/>
                        Imprimir
                      </Button>
                      <Button onClick={handleCopyLink} variant="outline">
                        <ExternalLink size={16} className="mr-2"/>
                        Copiar Link
                      </Button>
                    </div>
                  </Card>

                  <Card className="p-8">
                    <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">Link Direto</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Link publico associado ao identificador da tag devolvido pela API.
                    </p>
                    <code className="text-xs text-blue-700 break-all bg-blue-50 px-3 py-2 rounded block">
                      {qrCodeUrl}
                    </code>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                <Card className="p-6">
                  <h3 className="text-lg font-secondary font-bold text-slate-900 mb-4">Historico Completo de Ocorrencias</h3>
                  <HistoryList history={tagData.history} detailed/>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
};

const SessionMetric = ({label, value}) => (
  <div>
    <p className="text-slate-600">{label}</p>
    <p className="font-bold text-slate-900">{value || '-'}</p>
  </div>
);

const HistoryList = ({history, detailed = false}) => {
  if (history.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">Sem ocorrencias devolvidas pela API.</p>;
  }

  return (
    <div className="space-y-3">
      {history.map((incident) => (
        <div key={incident.id} className={`${detailed ? 'p-4' : 'p-3'} bg-slate-50 rounded-lg border border-slate-200`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-slate-900">{incident.type}</span>
            <span className="text-xs text-blue-600 font-medium">{incident.ticket}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>{incident.date || '-'}</span>
            <span className={incident.status === 'resolvido' ? 'text-emerald-600' : 'text-slate-400'}>
              {incident.status === 'resolvido' ? `Resolvido${incident.duration !== '-' ? ` em ${incident.duration}` : ''}` : incident.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
